import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker, Polyline } from "react-leaflet";
import L from "leaflet";
import { statusColor, num } from "@/lib/format";
import DroneNavigationMarker from "@/components/map/DroneNavigationMarker";
import DronePopupLayer from "@/components/map/DronePopupLayer";
import DronePopupContent from "@/components/map/DronePopupContent";
import { MapRotationController, useBearingAnimation, useElementSize } from "@/components/map/MapRotation";
import { buildNavState, appendSample, followView, isValidLatLng } from "@/lib/mapNavigation";
import { frameSize } from "@/lib/mapRotation";
import { popupReducer, popupLimits, POPUP_CLOSED } from "@/lib/mapPopup";

function pinIcon(color, label) {
  return L.divIcon({
    className: "drone-marker",
    html: `<div style="width:14px;height:14px;border-radius:50%;border:2px solid #0A0A0A;background:${color};box-shadow:0 0 6px ${color}"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

function wpIcon(seq) {
  return L.divIcon({
    className: "drone-marker",
    html: `<div class="map-upright" style="width:18px;height:18px;border-radius:3px;border:1px solid #3B82F6;background:#0A0A0A;color:#3B82F6;font:700 10px JetBrains Mono;display:flex;align-items:center;justify-content:center">${seq}</div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function baseIcon() {
  return L.divIcon({
    className: "drone-marker",
    html: `<div class="map-upright" style="width:20px;height:20px;border-radius:3px;border:2px solid #F59E0B;background:#F59E0B22;color:#F59E0B;display:flex;align-items:center;justify-content:center;box-shadow:0 0 8px rgba(245,158,11,0.5)"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" stroke-width="2.5"><path d="M3 10l9-7 9 7v10a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"/></svg></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function homeIcon() {
  // PX4 HOME — distinct (cyan "H") de la Base configurée (orange maison).
  return L.divIcon({
    className: "drone-marker",
    html: `<div class="map-upright" style="width:20px;height:20px;border-radius:50%;border:2px solid #22D3EE;background:#22D3EE22;color:#22D3EE;display:flex;align-items:center;justify-content:center;font:700 11px JetBrains Mono;box-shadow:0 0 8px rgba(34,211,238,0.55)">H</div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function cursorIcon() {
  return L.divIcon({
    className: "drone-marker",
    html: `<div class="pulse-ring" style="width:16px;height:16px;border-radius:50%;border:3px solid #2DD4BF;background:#0A0A0A;box-shadow:0 0 10px #2DD4BF"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || map.getZoom(), { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

const ATTRIBUTION = "Tiles © Esri — Esri, DeLorme, NAVTEQ";

// MAP.NAV.1 — historique d'échantillons par drone (horodatage télémétrie) + horloge de fraîcheur unique.
function useNavStates(drones) {
  const hist = useRef({});
  const [now, setNow] = useState(() => Date.now());
  const has = drones.length > 0;
  useEffect(() => {
    if (!has) return undefined;
    const id = setInterval(() => setNow(Date.now()), 2000);
    return () => clearInterval(id);
  }, [has]);
  return useMemo(() => {
    const out = {};
    drones.forEach((d) => {
      if (!d || d.drone_id == null) return;
      hist.current[d.drone_id] = appendSample(hist.current[d.drone_id], d);
      out[d.drone_id] = buildNavState(d, { nowMs: Date.now(), samples: hist.current[d.drone_id] });
    });
    return out;
  }, [drones, now]); // eslint-disable-line react-hooks/exhaustive-deps
}

// MAP.NAV.3 — état de la popup drone (contrat lib/mapPopup.js). Ancre = position valide courante, sinon
// dernière position valide connue (signalée invalide dans le contenu) : une perte GPS ne ferme pas la popup.
function useDronePopup({ drones, navs, selectedId, onSelect }) {
  const [popup, dispatch] = useReducer(popupReducer, POPUP_CLOSED);
  const lastPos = useRef({});
  Object.keys(navs).forEach((id) => { const n = navs[id]; if (n.positionValid) lastPos.current[id] = [n.lat, n.lng]; });
  const idsKey = drones.filter(Boolean).map((d) => d.drone_id).join("|");
  useEffect(() => { dispatch({ type: "PRESENCE", ids: idsKey ? idsKey.split("|") : [] }); }, [idsKey]);
  useEffect(() => { dispatch({ type: "EXTERNAL_SELECT", id: selectedId ?? null }); }, [selectedId]);
  const open = popup.openId != null;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") dispatch({ type: "CLOSE" }); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  const onMarkerClick = useCallback((id) => {
    dispatch({ type: "SELECT", id });
    if (selectRef.current) selectRef.current(id, { source: "map" });
  }, []);
  const closePopup = useCallback((seq) => dispatch({ type: "CLOSE", seq }), []);
  const popupDrone = open ? drones.find((d) => d && d.drone_id === popup.openId) : null;
  const n = popupDrone ? navs[popupDrone.drone_id] : null;
  const popupLatLng = n ? (n.positionValid ? [n.lat, n.lng] : lastPos.current[popupDrone.drone_id] || null) : null;
  return { popup, popupDrone: n ? popupDrone : null, popupLatLng, onMarkerClick, closePopup };
}

function MapClickClose({ onClose, active }) {
  const map = useMap();
  useEffect(() => {
    if (!active) return undefined;
    const h = () => onClose();
    map.on("click", h);
    return () => map.off("click", h);
  }, [map, active, onClose]);
  return null;
}

function RotationOverlay({ mapRef, northRef, rotating, notice }) {
  return (
    <>
      {rotating && (
        <div className="absolute top-2 right-2 z-[1001] flex flex-col gap-1 items-end pointer-events-auto">
          <div data-testid="map-north-indicator" title="Nord géographique"
            className="w-9 h-9 rounded-full bg-[#0A0A0A]/85 border border-white/20 flex items-center justify-center">
            <div ref={northRef} className="flex flex-col items-center leading-none">
              <span className="font-mono text-[9px] font-bold text-[#F97316]">N</span>
              <span className="block w-0 h-0 border-l-[4px] border-r-[4px] border-b-[9px] border-l-transparent border-r-transparent border-b-[#F97316]" />
            </div>
          </div>
          <button data-testid="map-zoom-in" onClick={() => mapRef.current && mapRef.current.zoomIn()}
            className="w-7 h-7 rounded-sm bg-[#141414] border border-white/15 text-white font-mono text-sm hover:bg-[#1f1f1f]">+</button>
          <button data-testid="map-zoom-out" onClick={() => mapRef.current && mapRef.current.zoomOut()}
            className="w-7 h-7 rounded-sm bg-[#141414] border border-white/15 text-white font-mono text-sm hover:bg-[#1f1f1f]">−</button>
        </div>
      )}
      {rotating && <div className="map-rot-attribution absolute bottom-0 right-0 z-[1001] px-1 font-mono text-[8px] text-white/45 bg-black/40 pointer-events-none">{ATTRIBUTION}</div>}
      {notice && (
        <div data-testid="map-follow-notice" className="absolute top-2 left-1/2 -translate-x-1/2 z-[1001] font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#F59E0B]/20 border border-[#F59E0B]/60 text-[#F59E0B] pointer-events-none">
          {notice}
        </div>
      )}
    </>
  );
}

export default function MapView({
  drones = [], selectedId, onSelect, recenter, destination, height = "100%",
  startPoint, plannedPath, realPath, waypoints, showPlanned = true, showReal = true,
  basePoint, baseLink, cursorPoint, onRealPathClick, showHealth = false,
  homePoint, homeLink, followId = null, onFollowChange, cursorNav = null, onMapReady,
}) {
  const initial = useMemo(() => {
    const d = drones.find((x) => x && isValidLatLng(x.lat, x.lng));
    return d ? [d.lat, d.lng] : [49.4944, 0.1079];
  }, [drones]);

  // MAP.NAV.1 — modèle de navigation partagé + SUIVRE (centre + cap vers le haut, fond tournant).
  const navs = useNavStates(drones);
  const following = followId != null;
  const fv = followView(following, following ? navs[followId] : null);
  const outerRef = useRef(null), innerRef = useRef(null), northRef = useRef(null), mapRef = useRef(null), dragRef = useRef(false);
  const [frameRotating, setFrameRotating] = useState(following);
  useEffect(() => { if (following) setFrameRotating(true); }, [following]);
  const followIdRef = useRef(followId);
  followIdRef.current = followId;
  const { bearingRef, kick } = useBearingAnimation({
    innerRef, northRef, targetDeg: fv.bearingDeg, holdRef: dragRef,
    onSettledNorth: () => { if (!dragRef.current) setFrameRotating((r) => (followIdRef.current != null ? r : false)); },
  });
  useEffect(() => { if (!following) kick(); }, [following, kick]);
  const vis = useElementSize(outerRef);
  const frame = frameSize(vis.w, vis.h, frameRotating);
  const followNav = following ? navs[followId] : null;
  const popupState = useDronePopup({ drones, navs, selectedId, onSelect });
  const { popup, popupDrone, popupLatLng, onMarkerClick, closePopup } = popupState;
  const limits = useMemo(() => popupLimits(vis.w || 320, vis.h || 320, following), [vis.w, vis.h, following]);

  return (
    <div data-testid="leaflet-map" ref={outerRef} data-following={following ? "1" : "0"} style={{ height, width: "100%", position: "relative", overflow: "hidden" }}>
    <div ref={innerRef} data-testid="map-rot-inner" className={`map-rot-inner ${frameRotating ? "is-rotating" : ""}`}
      style={frameRotating && vis.w ? { position: "absolute", left: frame.left, top: frame.top, width: frame.w, height: frame.h, transformOrigin: "50% 50%" } : { position: "absolute", inset: 0, transformOrigin: "50% 50%" }}>
    <MapContainer ref={(m) => { mapRef.current = m; if (m && onMapReady) onMapReady(m); }} center={initial} zoom={15} style={{ height: "100%", width: "100%" }} zoomControl={true}>
      <MapRotationController outerRef={outerRef} bearingRef={bearingRef} dragRef={dragRef} kick={kick} following={following}
        frameKey={`${frame.w}x${frame.h}`} followLatLng={fv.recenter && followNav ? [followNav.lat, followNav.lng] : null}
        onUserMove={() => onFollowChange && onFollowChange(false)} />
      <MapClickClose active={popup.openId != null} onClose={closePopup} />
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
        attribution='Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ'
        maxNativeZoom={16}
        maxZoom={19}
      />
      {recenter && <Recenter center={recenter} zoom={16} />}

      {/* Drone -> base link (dashed orange) */}
      {baseLink && baseLink.length === 2 && (
        <Polyline positions={baseLink} pathOptions={{ color: "#F59E0B", weight: 1.5, dashArray: "3 6", opacity: 0.7 }} />
      )}

      {/* UNIFIED.6 — Drone -> PX4 HOME link (dashed cyan, lecture seule, aucun calcul RTL) */}
      {homeLink && homeLink.length === 2 && (
        <Polyline data-testid="home-link" positions={homeLink} pathOptions={{ color: "#22D3EE", weight: 1.5, dashArray: "4 6", opacity: 0.75 }} />
      )}

      {/* Base configurée (CUSTOM) — distincte du HOME PX4 */}
      {basePoint && (
        <Marker position={[basePoint.lat, basePoint.lng]} icon={baseIcon()}>
          <Popup><div className="font-mono text-xs"><div className="font-bold">{basePoint.title || "Base / Home"}</div>
            {basePoint.label && <div>{basePoint.label}</div>}
            <div>{num(basePoint.lat, 5)}, {num(basePoint.lng, 5)}</div></div></Popup>
        </Marker>
      )}

      {/* UNIFIED.6 — PX4 HOME (live Raspberry). N'existe QUE si coordonnées valides (validées par le parent). */}
      {homePoint && Number.isFinite(homePoint.lat) && Number.isFinite(homePoint.lng) && (
        <Marker position={[homePoint.lat, homePoint.lng]} icon={homeIcon()}>
          <Popup><div className="font-mono text-xs space-y-0.5">
            <div className="font-bold">PX4 HOME</div>
            <div>Lat {num(homePoint.lat, 5)}</div>
            <div>Lon {num(homePoint.lng, 5)}</div>
            <div>Alt AMSL {homePoint.altAmsl == null ? "—" : `${num(homePoint.altAmsl, 1)} m`}</div>
            <div>Âge {homePoint.ageS == null ? "—" : `${num(homePoint.ageS, 1)} s`}</div>
          </div></Popup>
        </Marker>
      )}

      {/* Planned trajectory (dashed blue) */}
      {showPlanned && plannedPath && plannedPath.length > 1 && (
        <Polyline data-testid="planned-path" positions={plannedPath}
          pathOptions={{ color: "#3B82F6", weight: 2, dashArray: "6 8", opacity: 0.9 }} />
      )}

      {/* Real flown trajectory (solid teal) */}
      {showReal && realPath && realPath.length > 1 && (
        <Polyline data-testid="real-path" positions={realPath}
          pathOptions={{ color: "#2DD4BF", weight: 3, opacity: 0.85 }}
          eventHandlers={onRealPathClick ? { click: (e) => onRealPathClick(e.latlng) } : undefined} />
      )}

      {/* Replay cursor directionnel (MAP.NAV.1) : cap historique seulement s'il est enregistré */}
      {cursorPoint && cursorNav && (
        <DroneNavigationMarker nav={cursorNav} color="#2DD4BF" selected id="replay-cursor" zIndexOffset={1000}>
          <Popup><div className="font-mono text-xs"><div className="font-bold">Point sélectionné</div>
            <div>{num(cursorPoint.lat, 5)}, {num(cursorPoint.lng, 5)}</div>
            <div>Cap: {cursorNav.headingValid ? `${num(cursorNav.headingDeg, 0)}°` : "non enregistré"}</div></div></Popup>
        </DroneNavigationMarker>
      )}
      {cursorPoint && !cursorNav && (
        <Marker position={[cursorPoint.lat, cursorPoint.lng]} icon={cursorIcon()} zIndexOffset={1000}>
          <Popup><div className="font-mono text-xs"><div className="font-bold">Point sélectionné</div>
            <div>{num(cursorPoint.lat, 5)}, {num(cursorPoint.lng, 5)}</div></div></Popup>
        </Marker>
      )}

      {/* Start point */}
      {startPoint && (
        <Marker position={[startPoint.lat, startPoint.lng]} icon={pinIcon("#22C55E")}>
          <Popup><div className="font-mono text-xs"><div className="font-bold">Point de départ</div>
            <div>{num(startPoint.lat, 5)}, {num(startPoint.lng, 5)}</div></div></Popup>
        </Marker>
      )}

      {/* Waypoints (future multi-waypoint ready) */}
      {(waypoints || []).map((w, i) => (
        <Marker key={`wp-${i}`} position={[w.lat, w.lng]} icon={wpIcon((w.seq ?? i) + 1)}>
          <Popup><div className="font-mono text-xs"><div className="font-bold">Waypoint {(w.seq ?? i) + 1}</div>
            <div>{num(w.lat, 5)}, {num(w.lng, 5)}</div>{w.alt != null && <div>Alt: {num(w.alt)} m</div>}</div></Popup>
        </Marker>
      ))}

      {/* Drones (current position) — MAP.NAV.3 : popup CONTRÔLÉE unique (DronePopupLayer), non liée au marqueur */}
      {drones.filter((d) => d && navs[d.drone_id] && navs[d.drone_id].positionValid).map((d) => (
        <DroneNavigationMarker key={d.drone_id} nav={navs[d.drone_id]} color={statusColor(d)} id={d.drone_id}
          selected={selectedId === d.drone_id || following} active={d.status === "online"}
          onClick={() => onMarkerClick(d.drone_id)} />
      ))}
      {/* TELEMETRY.INDEPENDENT.FIELDS.1 — dernière position connue (périmée) clairement identifiée, jamais « actuelle ». */}
      {drones.filter((d) => d && navs[d.drone_id] && !navs[d.drone_id].positionValid && d.position_state === "stale"
        && isValidLatLng(d.last_known_lat, d.last_known_lng) && !(d.last_known_lat === 0 && d.last_known_lng === 0)).map((d) => (
        <CircleMarker key={`lk-${d.drone_id}`} center={[d.last_known_lat, d.last_known_lng]} radius={8}
          pathOptions={{ color: "#A3A3A3", fillColor: "#525252", fillOpacity: 0.25, weight: 2, dashArray: "4 4", className: `drone-last-known-${d.drone_id}` }}
          eventHandlers={{ click: () => onMarkerClick(d.drone_id) }}>
          <Popup><div data-testid={`drone-last-known-${d.drone_id}`} className="font-mono text-xs">{d.drone_id} — DERNIÈRE POSITION CONNUE (PÉRIMÉE)<br />
            {num(d.last_known_lat, 5)}, {num(d.last_known_lng, 5)}</div></Popup>
        </CircleMarker>
      ))}
      {popupDrone && popupLatLng && (
        <DronePopupLayer seq={popup.seq} latlng={popupLatLng} limits={limits} following={following} rotating={frameRotating}
          bearingRef={bearingRef} outerRef={outerRef} dragRef={dragRef} onClose={closePopup}>
          <DronePopupContent d={popupDrone} nav={navs[popupDrone.drone_id]} showHealth={showHealth} />
        </DronePopupLayer>
      )}

      {/* Destination */}
      {destination && (
        <CircleMarker center={[destination.lat, destination.lng]} radius={9}
          pathOptions={{ color: "#3B82F6", fillColor: "#3B82F6", fillOpacity: 0.4, weight: 2 }}>
          <Popup>
            <div className="font-mono text-xs">
              <div className="font-bold">Destination</div>
              <div>{num(destination.lat, 5)}, {num(destination.lng, 5)}</div>
            </div>
          </Popup>
        </CircleMarker>
      )}
    </MapContainer>
    </div>
    <RotationOverlay mapRef={mapRef} northRef={northRef} rotating={frameRotating} notice={fv.notice} />
    </div>
  );
}
